import React, { Suspense, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import MainLayout from "./layout/MainLayout";
import AuthLayout from "./layout/AuthLayout";
import LoadingSpinner from "./components/UI/loadingSpinner/LoadingSpinner";
import "./scss/App.scss";
import "react-quill/dist/quill.snow.css";
import UserControl from "./pages/UserControl";
import ApplicationView from "./pages/ApplicationView";
import { useSelector } from "react-redux";
import { RootState } from "./store/store";
import { User } from "./api/authApi";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LoginContext from "./store/loginContext";
import ProtectedRoute from "./route/ProtectedRoute";

// Lazy-loaded pages
const Login = React.lazy(() => import("./pages/Login"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

// Temp extra pages
const Careers = React.lazy(() => import("./pages/Careers"));
const CareerEdit = React.lazy(() => import("./pages/CareerEdit"));
const Applications = React.lazy(() => import("./pages/Applications"));
const ApplicationEdit = React.lazy(() => import("./pages/ApplicationEdit"));
const CareerView = React.lazy(() => import("./pages/CareerView"));
const CareerAdd = React.lazy(() => import("./pages/CareerAdd"));
const Documents = React.lazy(() => import("./pages/Documents"));

function App() {
  const user = useSelector(
    (state: RootState) => state.auth.user as User | null
  );
  const loginCtx = useContext(LoginContext);

  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "Poppins",
            fontSize: "14px",
          },
        }}
      />
      <Suspense fallback={<LoadingSpinner fullPage />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={loginCtx?.isLogin ? <Navigate to="/" /> : <Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AuthLayout />}>
              <Route path="/" element={<MainLayout />}>

                {/* User Control */}
                {user?.can_read && (
                  <>
                    {user?.can_write && (
                      <>
                        <Route path="user-control" element={<UserControl />} />
                        <Route path="careers/edit/:jobId" element={<CareerEdit />} />
                        <Route path="careers/view/:jobId" element={<CareerView />} />
                        <Route path="careers/add" element={<CareerAdd />} />
                        <Route
                          path="applications/:applicationId"
                          element={<ApplicationEdit />}
                        />
                      </>
                    )}

                    <Route index element={<Navigate to="/careers" replace />} />

                    {/* Careers */}
                    <Route path="careers" element={<Careers />} />

                    {/* Applications */}
                    <Route path="applications" element={<Applications />} />
                    <Route
                      path="applications/view/:applicationId"
                      element={<ApplicationView />}
                    />
                    <Route path="documents" element={<Documents />} />
                  </>
                )}

              </Route>
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
